"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { cn } from "@/lib/utils";
import { usePayment, useUser } from "@/stores/appStore";
import { useVipRenewalIncentiveRuntime } from "@/stores/vipRenewalIncentiveRuntimeStore";
import type { TouchpointItem } from "@/stores/slices/guideSlice";
import {
  getActiveVipIncentiveOffer,
  getVipIncentiveCopy,
  inactiveVipIncentiveOffer,
  VipIncentiveOffer,
  VIP_RENEWAL_INCENTIVE_BACKGROUND,
  useVipRenewalIncentiveCountdown,
} from "@/components/payment/vipRenewalIncentive";

function CountdownUnit({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  return (
    <div
      className={cn(
        "flex h-[95px] w-[105px] flex-col items-center rounded-xl",
        "bg-[rgb(var(--vip-renewal-incentive-countdown-bg)/0.7)]",
      )}
    >
      <span className="mt-[21px] h-6 text-center text-[22px] font-medium leading-6 text-foreground">
        {value}
      </span>
      <span className="mt-[7px] h-6 text-center text-lg font-normal leading-6 text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

function CountdownSeparator() {
  return (
    <span className="mb-[17px] flex h-6 w-[26px] items-center justify-center text-[22px] leading-6 text-annotation-foreground">
      :
    </span>
  );
}

function isModalEntryPath(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  return normalizedPathname === "/papers";
}

function getTouchpointVipOffer(item?: TouchpointItem | null): VipIncentiveOffer {
  if (!item) {
    return inactiveVipIncentiveOffer;
  }
  const remainingSeconds = Number(item.payload?.remainingSeconds);
  const endTime = Number(item.payload?.endTime);
  if (!Number.isFinite(remainingSeconds) || !Number.isFinite(endTime)) {
    return inactiveVipIncentiveOffer;
  }
  if (item.type === "first_offer") {
    const amount = Number(item.payload?.bonusBeans);
    if (!Number.isFinite(amount) || amount <= 0) {
      return inactiveVipIncentiveOffer;
    }
    return {
      active: true,
      kind: "first",
      remainingSeconds,
      amount,
      endTime,
    };
  }
  if (item.type === "recovery_offer") {
    const amount = Number(item.payload?.recoverableAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return inactiveVipIncentiveOffer;
    }
    return {
      active: true,
      kind: "recovery",
      remainingSeconds,
      amount,
      endTime,
    };
  }
  return inactiveVipIncentiveOffer;
}

type VipRenewalIncentiveDialogProps = {
  item?: TouchpointItem | null;
  onDismiss?: (item: TouchpointItem) => Promise<void> | void;
};

export function VipRenewalIncentiveDialog({
  item,
  onDismiss,
}: VipRenewalIncentiveDialogProps = {}) {
  const pathname = usePathname();
  const {
    user,
    firstOffer,
    recoveryOffer,
    expireVipIncentiveOffer,
    refreshUserInfo,
  } = useUser();
  const { openPayment } = usePayment();
  const {
    dismissedEndTimes,
    setDismissedEndTime,
  } = useVipRenewalIncentiveRuntime();
  const requestedUserIdRef = useRef<number | null>(null);
  const dialogContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!user?.userId || requestedUserIdRef.current === user.userId) {
      return;
    }

    requestedUserIdRef.current = user.userId;
    void refreshUserInfo();
  }, [refreshUserInfo, user?.userId]);

  const incentiveOffer = useMemo(
    () => item ? getTouchpointVipOffer(item) : getActiveVipIncentiveOffer({
      firstOffer,
      recoveryOffer,
    }),
    [firstOffer, item, recoveryOffer],
  );
  const handleOfferExpire = useCallback((offer: { kind: "first" | "recovery" }) => {
    if (item) {
      return;
    }
    if (offer.kind === "first") {
      void refreshUserInfo();
      return;
    }
    expireVipIncentiveOffer(offer.kind);
  }, [expireVipIncentiveOffer, item, refreshUserInfo]);
  const {
    remainingSeconds,
    countdown,
    offerBeans,
  } = useVipRenewalIncentiveCountdown(incentiveOffer, handleOfferExpire);
  const copy = useMemo(
    () => getVipIncentiveCopy({
      offer: incentiveOffer,
      offerBeans,
    }),
    [incentiveOffer, offerBeans],
  );
  const offerEndTime = incentiveOffer.active ? incentiveOffer.endTime : 0;
  const modalDismissedEndTime = incentiveOffer.active
    ? dismissedEndTimes.modal[incentiveOffer.kind]
    : 0;

  const dismissalEndTime = offerEndTime;
  const open = item
    ? incentiveOffer.active && remainingSeconds > 0
    : incentiveOffer.active
      && isModalEntryPath(pathname)
      && remainingSeconds > 0
      && modalDismissedEndTime !== dismissalEndTime;

  const dismissCurrentOffer = useCallback(() => {
    if (item) {
      void onDismiss?.(item);
      return;
    }
    if (incentiveOffer.active && dismissalEndTime > 0) {
      setDismissedEndTime("modal", incentiveOffer.kind, dismissalEndTime);
    }
  }, [dismissalEndTime, incentiveOffer, item, onDismiss, setDismissedEndTime]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      dismissCurrentOffer();
    }
  }, [dismissCurrentOffer]);

  const handleUpgrade = useCallback(() => {
    dismissCurrentOffer();
    openPayment({
      open: true,
      type: "upgrade",
    });
  }, [dismissCurrentOffer, openPayment]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/60",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 duration-150",
          )}
        />
        <DialogPrimitive.Content
          ref={dialogContentRef}
          tabIndex={-1}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            dialogContentRef.current?.focus({ preventScroll: true });
          }}
          className={cn(
            "fixed left-1/2 top-1/2 z-[51] h-[592px] w-[650px]",
            "max-h-[calc(100vh-32px)] max-w-[calc(100vw-32px)]",
            "-translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg border-0 bg-background p-0",
            "shadow-lg outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 duration-150",
          )}
          style={{
            backgroundImage: `url(${VIP_RENEWAL_INCENTIVE_BACKGROUND})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <DialogPrimitive.Title className="sr-only">
            {copy.badge}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {copy.title}，倒计时结束后活动关闭。
          </DialogPrimitive.Description>

          <DialogPrimitive.Close asChild>
            <button
              type="button"
              aria-label="关闭限时会员礼"
              className="absolute right-5 top-5 z-10 flex size-4 items-center justify-center border-0 bg-transparent p-0 text-annotation-foreground transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <X className="size-4" strokeWidth={2} />
            </button>
          </DialogPrimitive.Close>

          <div
            className="absolute left-[278px] top-[202px] flex h-[28px] w-[96px] items-center justify-center rounded border text-sm font-medium leading-none"
            style={{
              borderColor: "var(--vip-renewal-incentive-badge-border)",
              color: "var(--vip-renewal-incentive-badge-foreground)",
            }}
          >
            {copy.badge}
          </div>

          <h2
            className="absolute left-[45px] top-[239px] h-[45px] w-[560px] whitespace-nowrap bg-clip-text text-center text-[32px] font-semibold leading-[45px] text-transparent"
            style={{
              backgroundImage: "var(--vip-renewal-incentive-title-gradient)",
            }}
          >
            {copy.title}
          </h2>

          <p className="absolute left-[208px] top-[294px] h-[19px] w-[234px] text-center text-sm font-normal leading-[19px] text-foreground">
            {copy.subtitle}
          </p>

          <div className="absolute left-[142px] top-[365px] grid h-[95px] w-[367px] grid-cols-[105px_26px_105px_26px_105px] items-center">
            <CountdownUnit value={countdown.hours} label="时" />
            <CountdownSeparator />
            <CountdownUnit value={countdown.minutes} label="分" />
            <CountdownSeparator />
            <CountdownUnit value={countdown.seconds} label="秒" />
          </div>

          <div className="absolute left-[63px] top-[510px] flex h-10 w-[524px] gap-6">
            <button
              type="button"
              className={cn(
                "h-10 w-[250px] rounded border border-border bg-background text-sm font-normal leading-6 text-foreground",
                "transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              )}
              onClick={dismissCurrentOffer}
            >
              再考虑一下
            </button>
            <button
              type="button"
              className={cn(
                "h-10 w-[250px] rounded text-sm font-medium leading-6 text-background",
                "transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              )}
              style={{
                background: "var(--vip-renewal-incentive-button-gradient)",
              }}
              onClick={handleUpgrade}
            >
              {copy.modalCtaLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
